/**
 * Simple HTML to Markdown converter for label descriptions
 * Handles conversion from TipTap HTML to markdown without complex tool annotations
 */
export class HtmlToMarkdownConverter {
  constructor() {
    // DOM parser for parsing HTML content
    this.parser = new DOMParser();
  }

  /**
   * Convert HTML content from TipTap editor to markdown
   * @param {string} html - The HTML content from TipTap editor
   * @returns {string} - Markdown content
   */
  convertToMarkdown(html) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    // Parse the HTML
    const doc = this.parser.parseFromString(html, 'text/html');
    // Process the document and convert to markdown
    return this.processNode(doc.body).trim();
  }

  /**
   * Process a DOM node and convert it to markdown
   * @param {Node} node - The DOM node to process
   * @returns {string} - Markdown representation
   */
  processNode(node) {
    if (!node) return '';

    switch (node.nodeType) {
      case Node.TEXT_NODE:
        return HtmlToMarkdownConverter.escapeMarkdownCharacters(
          node.textContent
        );
      case Node.ELEMENT_NODE:
        return this.processElement(node);
      default:
        return '';
    }
  }

  /**
   * Process an HTML element and convert it to markdown
   * @param {Element} element - The HTML element to process
   * @returns {string} - Markdown representation
   */
  processElement(element) {
    const tagName = element.tagName.toLowerCase();
    const content = this.processChildNodes(element);

    switch (tagName) {
      case 'p':
        return content + '\n\n';
      case 'br':
        return '\n';
      case 'strong':
      case 'b':
        return `**${content}**`;
      case 'em':
      case 'i':
        return `*${content}*`;
      case 'code':
        return `\`${content}\``;
      case 'ul':
        return content + '\n';
      case 'ol':
        return content + '\n';
      case 'li':
        return `- ${content}\n`;
      case 'h1':
        return `# ${content}\n\n`;
      case 'h2':
        return `## ${content}\n\n`;
      case 'h3':
        return `### ${content}\n\n`;
      case 'h4':
        return `#### ${content}\n\n`;
      case 'h5':
        return `##### ${content}\n\n`;
      case 'h6':
        return `###### ${content}\n\n`;
      case 'a': {
        const href = element.getAttribute('href');
        return href ? `[${content}](${href})` : content;
      }
      case 'img': {
        const src = element.getAttribute('src');
        const alt = element.getAttribute('alt') || '';
        return src ? `![${alt}](${src})` : '';
      }
      case 'blockquote':
        return `> ${content}\n\n`;
      default:
        return content;
    }
  }

  /**
   * Process all child nodes of an element
   * @param {Element} element - The parent element
   * @returns {string} - Processed content of all child nodes
   */
  processChildNodes(element) {
    let result = '';
    // Convert NodeList to array and iterate
    Array.from(element.childNodes).forEach(child => {
      result += this.processNode(child);
    });
    return result;
  }

  /**
   * Escape special markdown characters in text content
   * For label descriptions, we want minimal escaping to preserve readability
   * @param {string} text - The text to escape
   * @returns {string} - Text with minimal escaping
   */
  static escapeMarkdownCharacters(text) {
    if (!text) return '';

    // For label descriptions, only escape characters that would break markdown structure
    // Keep it minimal to preserve natural text readability
    return text
      .replace(/\\/g, '\\\\') // Escape backslashes
      .replace(/`/g, '\\`'); // Escape backticks to prevent code blocks

    // Don't escape other characters as they're part of natural text
    // The rich editor will handle the display properly
  }
}

// Create a default instance for easy importing
const htmlToMarkdownConverter = new HtmlToMarkdownConverter();
export default htmlToMarkdownConverter;
